/* eslint-disable @next/next/no-img-element -- official network logo asset
   files from public/icons/social (real brand marks, colors baked into the
   SVG files), same pattern as the press-logo band. */
import Link from "next/link"
import { SOCIAL_MEDIA_ACCOUNTS } from "@/lib/social-media-config"
import { GOOGLE_FOLLOW_URL } from "@/lib/preferred-source"
import { PreferredSourceLink } from "@/components/chrome/preferred-source-link"

/**
 * SocialFollowRow — the ONE shared "follow us" icon rail for every surface that
 * links the official Fridge.com social profiles (footer, and any header / blog /
 * press follow row that needs it).
 *
 * Owner spec 07/20 (reference: the footer FOLLOW US row screenshot — that image
 * IS the spec):
 *  - REAL official logos from asset files (public/icons/social/*.svg): the
 *    Pinterest red badge, Instagram outline in its brand pink, YouTube red
 *    play badge, and the MULTICOLOR Google G exactly as Google draws it.
 *  - FULL icons — no padding chips, no borders, no circles around them.
 *  - ALWAYS colorful at rest; hover scales slightly, never reveals color.
 *  - One line, always. Pointer cursor, aria-labels, visible focus rings.
 *
 * No client hooks of its own: it renders safely inside the footer's
 * server/client dual tree. The Google entry delegates to PreferredSourceLink,
 * which is a client component but takes no hooks either.
 */

// The preferred-sources URL now lives in lib/preferred-source.ts alongside
// Google's own popup mechanics (FRIDGE-876). Re-exported under its historical
// name for the modules that already import it from here — one definition, not
// a second copy.
export { GOOGLE_FOLLOW_URL }

// The logo IS the link — no chip, no border, no background. Pointer cursor,
// slight scale on hover (color stays on), visible focus ring.
const LINK_CLS =
  "social-icon-control"

type FollowNet = {
  key: string
  label: string
  url: string
  src: string
}

const NETS: FollowNet[] = [
  { key: "pinterest", label: "Pinterest", url: SOCIAL_MEDIA_ACCOUNTS.pinterest.url, src: "/icons/social/pinterest.svg" },
  { key: "instagram", label: "Instagram", url: SOCIAL_MEDIA_ACCOUNTS.instagram.url, src: "/icons/social/instagram.svg" },
  { key: "youtube", label: "YouTube", url: SOCIAL_MEDIA_ACCOUNTS.youtube.url, src: "/icons/social/youtube.svg" },
  { key: "google", label: "Google", url: GOOGLE_FOLLOW_URL, src: "/icons/social/google.svg" },
]

interface SocialFollowRowProps {
  /** Extra classes on the outer wrapper. */
  className?: string
  /** Leading label. Pass null to hide it. Defaults to "Follow us". */
  label?: string | null
}

export function SocialFollowRow({ className = "", label = "Follow us" }: SocialFollowRowProps) {
  return (
    <div className={`flex flex-nowrap items-center gap-2 ${className}`}>
      {label && (
        <span className="text-sm font-semibold text-black uppercase tracking-wide hidden sm:inline mr-1 shrink-0">
          {label}
        </span>
      )}
      {NETS.map((n) => {
        const logo = <img src={n.src} alt="" width={26} height={26} className="w-[26px] h-[26px]" loading="lazy" decoding="async" />
        // The Google G is the preferred-sources flow, not a profile link, so it
        // opens Google's centered popup rather than navigating the reader away
        // (FRIDGE-876). Same URL, same rel, same look — only the click differs.
        if (n.key === "google") {
          return (
            <PreferredSourceLink
              key={n.key}
              className={LINK_CLS}
              rel="nofollow noopener noreferrer"
              title={`Follow Fridge.com on ${n.label}`}
              ariaLabel={`Follow Fridge.com on ${n.label}`}
            >
              {logo}
            </PreferredSourceLink>
          )
        }
        return (
          <Link
            key={n.key}
            href={n.url}
            className={LINK_CLS}
            target="_blank"
            rel="nofollow noopener noreferrer"
            title={`Follow Fridge.com on ${n.label}`}
            aria-label={`Follow Fridge.com on ${n.label}`}
            prefetch={false}
          >
            {logo}
          </Link>
        )
      })}
    </div>
  )
}
